/* ─────────────────────────────────────────
   courtDeposit — single source of truth for "how much does the buyer pay
   online for THIS court booking".

   The deposit used to be a club-wide setting (`club.reservationMode` +
   `club.depositAmount`). It is now a per-pricing-row field: the backend
   resolves price and deposit from the SAME winning row and derives the
   reservation mode from it (see helper/payments/reservationCheckoutBuilder.js
   in the API — it deliberately no longer reads `club.reservationMode` on the
   court path; that field is the bars' now).

   The public availability/price endpoints do not expose `deposit` per slot
   yet, so `resolveSlotDeposit` falls back to the legacy club-wide reading
   whenever the field is absent from the payload. The moment the API starts
   sending `deposit` on a slot — even as `null` — the per-row value wins and
   the fallback goes dead on its own.
───────────────────────────────────────── */

// True once the API sends `deposit` on the slot, `null` included: `null` is a
// meaningful value here ("this row charges nothing online"), so presence, not
// truthiness, is what tells the two contracts apart.
export function slotCarriesDeposit(slot) {
  return Boolean(slot) && Object.prototype.hasOwnProperty.call(slot, "deposit");
}

/**
 * Amount charged online for the selected slot, in pesos. 0 means the whole
 * price is paid at the club.
 */
export function resolveSlotDeposit(slot, club) {
  if (slotCarriesDeposit(slot)) {
    const deposit = Number(slot.deposit);
    if (slot.deposit == null || !Number.isFinite(deposit) || deposit <= 0) return 0;
    const price = Number(slot.price);
    // Mirrors the backend's cap: a stale row must never charge more than the
    // booking itself is worth.
    return Number.isFinite(price) && price > 0 ? Math.min(deposit, price) : deposit;
  }

  if (club?.reservationMode === "deposit") return Number(club.depositAmount) || 0;
  return 0;
}

/**
 * Whether the buyer is sent to the payment gateway for this slot.
 *
 * Under the legacy contract this is NOT the same question as "is the deposit
 * greater than zero": a club in "deposit" mode with no `depositAmount` still
 * went through checkout. Only the per-row contract ties the two together.
 */
export function slotRequiresOnlinePayment(slot, club) {
  if (slotCarriesDeposit(slot)) return resolveSlotDeposit(slot, club) > 0;
  return club?.reservationMode === "deposit";
}
